import React, { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOrderOnGetCartOrdersDTO from "../../../models/DTO/IOrderOnGetCartOrdersDTO";
import IOrderGetCartOrdersDTO from "../../../models/DTO/IOrderGetCartOrdersDTO";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import IOrder from "../../../models/IOrder";
import styled from "styled-components";
import OwTable from "../../shared/OwTable/OwTable";
import IOwTableCols from "../../../models/IOwTableCols";
import { EcommerceOrderDetailRow } from "../EcommerceOrderDetailRow/EcommerceOrderDetailRow";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwButton from "../../shared/OwButton/OwButton";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import { Socket } from "socket.io-client";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import { SearchSection } from "@components/shared/SearchSection/SearchSection";
import CartService from "@services/CartService";
import HelperService from "../../../services/HelperService";
import UserService from "../../../services/UserService";
import CartStatusEnum from "../../../models/CartStatusEnum";
import { IInactiveCartSummary } from "../../../models/DTO/IOnGetInactiveCartsDTO";

type Props = {};

const OrderSearchDiv = styled.div`
    margin-left: auto;
    display: flex;
    flex-direction: column;
`;

const OrderTableDiv = styled.div`
    margin-top: 1rem;
`;

const INACTIVE_CARTS_POLL_INTERVAL_MS = 5000;

const InactiveCartRowDiv = styled.div<{ $isError?: boolean }>`
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    margin-bottom: 0.5rem;
    border-radius: 0.5rem;
    cursor: pointer;
    background-color: ${({ $isError }) => ($isError ? "rgba(220, 53, 69, 0.1)" : "rgba(248, 153, 56, 0.1)")};
    border: 1px solid ${({ $isError }) => ($isError ? "rgba(220, 53, 69, 0.3)" : "rgba(248, 153, 56, 0.3)")};

    &:hover {
        background-color: ${({ $isError }) => ($isError ? "rgba(220, 53, 69, 0.18)" : "rgba(248, 153, 56, 0.18)")};
    }
`;

const InactiveCartIconDiv = styled.div<{ $isError?: boolean }>`
    margin-right: 0.75rem;
    color: ${({ $isError }) => ($isError ? "rgb(220, 53, 69)" : "rgb(248, 153, 56)")};
    font-size: 1.25rem;
`;

const getInactiveCartDisplay = (status: CartStatusEnum): { iconClass: string; labelKey: string; isError: boolean } => {
    switch (status) {
        case CartStatusEnum.PENDING:
            return { iconClass: "fad fa-spinner fa-spin", labelKey: "pending_cart_processing", isError: false };
        case CartStatusEnum.PAYMENT_PENDING:
            return { iconClass: "fad fa-pause-circle", labelKey: "pending_cart_awaiting_payment", isError: false };
        case CartStatusEnum.FAILED:
            return { iconClass: "fad fa-exclamation-circle", labelKey: "pending_cart_failed", isError: true };
        case CartStatusEnum.PAYMENT_ERRORED:
        default:
            return { iconClass: "fad fa-exclamation-circle", labelKey: "pending_cart_payment_errored", isError: true };
    }
};

export const EcommerceOrders: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);

    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);
    const navigate = useNavigate();

    const [orders, setOrders] = useState<IOrder[]>([]);
    const [initialLoad, setInitialLoad] = useState(true);
    const [isDataFetching, setIsDataFetching] = useState(false);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [isReset, setIsReset] = useState(false);
    const [paginationChanged, setPaginationChanged] = useState(false);
    const [totalOrders, setTotalOrders] = useState<number>();
    const [orderSearch, setOrderSearch] = useState("");
    const [poNoSearch, setPoNoSearch] = useState("");
    const [queryString, setQueryString] = useState("");
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [inactiveCarts, setInactiveCarts] = useState<IInactiveCartSummary[]>([]);
    const previousInactiveCountRef = useRef(0);
    const getCartOrdersRef = useRef<() => void>(() => {});

    useEffect(() => {
        socket.on(EndpointsEnum.OrderOnGetCartOrders, onGetCartOrders);

        getCartOrders();

        document.title = "Orderwerks >> Orders";

        return () => {
            socket.removeListener(EndpointsEnum.OrderOnGetCartOrders, onGetCartOrders);
        };
    }, []);

    useEffect(() => {
        getCartOrdersRef.current = getCartOrders;
    });

    const fetchInactiveCarts = async () => {
        try {
            const response = await CartService.getInactiveCarts(
                userModel,
                supplierModel,
                userModel.isSupplierUser ? undefined : vendorModel
            );
            setInactiveCarts(response.carts ?? []);
        } catch (err) {
            console.warn("Failed to fetch inactive carts", err);
        }
    };

    useEffect(() => {
        fetchInactiveCarts();
        const intervalId = setInterval(fetchInactiveCarts, INACTIVE_CARTS_POLL_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, []);

    // When the inactive-cart count shrinks, a cart finished processing — refresh
    // the orders list so the new order shows up.
    useEffect(() => {
        const prev = previousInactiveCountRef.current;
        if (prev > inactiveCarts.length) {
            getCartOrdersRef.current();
        }
        previousInactiveCountRef.current = inactiveCarts.length;
    }, [inactiveCarts.length]);

    const getCartOrders = () => {
        const payload: IOrderGetCartOrdersDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            limit,
            offset,
        };

        if (queryString.length) {
            payload.queryString = queryString;
        }

        socket.emit(EndpointsEnum.OrderGetCartOrders, payload);
    };

    const onGetCartOrders = (data: IOrderOnGetCartOrdersDTO) => {
        setOrders(data.orders);
        setTotalOrders(data.totalOrders);

        window.document.getElementById("mainView")!.scrollTo({ top: 0 });
    };

    useEffect(() => {
        setupQueryString();
    }, [orderSearch, poNoSearch]);

    useEffect(() => {
        if (isReset) {
            search();
        }
    }, [isReset]);

    useEffect(() => {
        if (paginationChanged) {
            search();
        }
    }, [paginationChanged]);

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        console.log("handleChange: ", value);

        switch (name) {
            case "order":
                setOrderSearch(value);
                break;
            case "po_no":
                setPoNoSearch(value);
                break;
        }
    };

    const setupQueryString = () => {
        let _queryString = "";

        const queryArr: string[] = [];

        if (orderSearch.length) {
            queryArr.push(`order=${orderSearch}`);
        }

        if (poNoSearch.length) {
            queryArr.push(`po_no=${poNoSearch}`);
        }

        if (queryArr.length) {
            _queryString = "?" + queryArr.join("&");
        }

        setQueryString(_queryString);
    };

    const search = () => {
        getCartOrders();

        setIsReset(false);
        setPaginationChanged(false);
    };

    const reset = () => {
        setOrderSearch("");
        setPoNoSearch("");
        setQueryString("");
        setIsReset(true);
    };

    const onPaginationChanged = (_offset: number, _limit: number) => {
        setLimit(_limit);
        setOffset(_offset);
        setPaginationChanged(true);
    };

    return (
        <div className={"EcommerceOrders"}>
            <div className={"container mt-3 mb-5"}>
                <div className={"row"}>
                    <div className={"col-12 d-flex flex-row align-items-center justify-content-between"}>
                        <OwTitle className={"me-2"} title="orders" leftIconName="fad fa-list-alt me-2" />

                        <div>
                            {totalOrders ? (
                                <div className={"bg-secondary text-white rounded-pill px-2 py-1"}>
                                    {totalOrders} <OwLabel title={totalOrders === 1 ? "order" : "orders"} />
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>

                {inactiveCarts.length > 0 ? (
                    <div className={"mt-3"}>
                        <div className={"mb-2"}>
                            <OwLabel title={"pending_cart_banner_title"} isBold={true} />
                        </div>
                        {inactiveCarts.map((cart) => {
                            const display = getInactiveCartDisplay(cart.status);
                            const total = HelperService.formatCurrency({
                                value: cart.total || 0,
                                currency: "USD",
                                precision: supplierModel.pricing_precision || 2,
                            });
                            return (
                                <InactiveCartRowDiv
                                    key={cart.cartGuid}
                                    $isError={display.isError}
                                    onClick={() => navigate(`/ecomm/inactive-cart/${cart.cartGuid}`)}>
                                    <InactiveCartIconDiv $isError={display.isError}>
                                        <i className={display.iconClass}></i>
                                    </InactiveCartIconDiv>
                                    <div className={"flex-grow-1"}>
                                        <div>
                                            <OwLabel title={display.labelKey} isBold={true} />
                                        </div>
                                        <div className={"text-small"}>
                                            {cart.itemCount} <OwLabel title={cart.itemCount === 1 ? "item" : "items"} />
                                            {!hidePricing ? <> · {total}</> : null}
                                        </div>
                                    </div>
                                    <div>
                                        <i className={"fas fa-chevron-right"}></i>
                                    </div>
                                </InactiveCartRowDiv>
                            );
                        })}
                    </div>
                ) : null}

                <SearchSection>
                    <div className={"row"}>
                        <div className={"col-12 col-md-6 d-flex flex-column flex-sm-row align-items-start align-items-sm-end gap-2"}>
                            <div className={"w-100 w-sm-auto"}>
                                <div>
                                    <OwLabel title={"order"} />
                                </div>
                                <div>
                                    <OwFnInput enableDebounce={false} fn={handleChange} name={"order"} value={orderSearch} />
                                </div>
                            </div>
                            <div className={"w-100 w-sm-auto"}>
                                <div>
                                    <OwLabel title={"po_no"} />
                                </div>
                                <div>
                                    <OwFnInput enableDebounce={false} fn={handleChange} name={"po_no"} value={poNoSearch} />
                                </div>
                            </div>
                            <div className={"d-flex"}>
                                <OwButton title={"search"} fn={search} active={isSearchActive} />
                                <OwButton className={"btn-danger ms-2"} title={"reset"} fn={reset} active={isSearchActive} />
                            </div>
                        </div>
                    </div>
                </SearchSection>

                <div className={"mt-3"}>
                    {orders.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="fad fa-receipt fa-3x text-muted mb-3 d-block"></i>
                            <p className="text-muted mb-0">
                                <OwLabel title="no_orders_found" />
                            </p>
                        </div>
                    ) : (
                        orders.map((order) => {
                            return <EcommerceOrderDetailRow key={order.guid} order={order} />;
                        })
                    )}
                </div>

                <div>
                    {totalOrders ? (
                        <PaginationBar offset={offset} limit={limit} totalRecords={totalOrders} onPaginationChanged={onPaginationChanged} />
                    ) : null}
                </div>
            </div>
        </div>
    );
};
